/**
 * BehaviorGraph ReactFlow Editor
 *
 * Visual node graph editor for authoring BehaviorGraph data.
 * Custom node types with editable config fields, inline value editors,
 * flow + data handles, add-node palette with templates, save button.
 * Supports TRACE_subgraph: group nodes into subgraphs, navigate into them,
 * and inline them back.
 *
 * Architecture: BehaviorGraphEditor (outer) manages navigation and allGraphs.
 * GraphView (inner) manages ReactFlow state for a single graph view and remounts
 * via `key` on navigation to guarantee clean state.
 */

import React, { useCallback, useEffect, useState, useMemo, useRef } from 'react';
import ReactFlow, {
    Controls,
    Background,
    addEdge,
    useNodesState,
    useEdgesState,
    Handle,
    Position,
    useReactFlow,
    useEdges,
    SelectionMode,
} from 'reactflow';
import type {
    Node,
    Edge,
    Connection,
    OnConnect,
    NodeTypes,
    NodeProps,
} from 'reactflow';
import { GraphOps, MACRO_OP_PREFIX, Properties } from 'trace-viewer/behaviors';
import type { GraphData, GraphVariable } from 'trace-viewer/behaviors';
import type { SceneObject } from 'trace-viewer/scene';
import { resolveStateOwner, collectScopedStateTargets } from 'trace-viewer/editor';
import { NODE_DEFS, NODE_COLORS, MACRO_BADGE, getNodeDef, defaultInlineValues, type NodeDef, type NodeCategory, type SocketDef, type SocketType } from './node-definitions';
import {
    graphToReactFlow,
    reactFlowToGraph,
    groupIntoSubgraph,
    inlineSubgraph,
    updateInterfaceFromPortEdges,
    remapVariablesToCanonical,
    ALL_TYPE_SIGNATURES,
    TYPE_COMPONENT_COUNT,
    type FlowNodeData,
} from './graph-conversion';
import { expandMacroNode } from './macro-expand';
import { SAMPLE_GRAPHS } from './sample-graphs';
import { useEditorContext, EditorContextProvider } from './EditorContext';
import { SceneObjectPicker, type SceneObjectRef } from '../interactions-editor-reactflow/SceneObjectPicker';
import { AnimationClipPicker } from '../behaviors/animation-clip-picker';

// ============================================================================
// Shared styles
// ============================================================================

const INPUT_STYLE: React.CSSProperties = {
    background: '#1a1a1a',
    border: '1px solid #555',
    borderRadius: 3,
    color: '#ddd',
    fontSize: 10,
    padding: '2px 4px',
    width: '100%',
    outline: 'none',
};

const CHECKBOX_LABEL: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    fontSize: 10,
    color: '#aaa',
    cursor: 'pointer',
};

const TOOLBAR_BUTTON: React.CSSProperties = {
    padding: '6px 14px',
    background: '#333',
    border: '1px solid #555',
    borderRadius: 4,
    color: '#ddd',
    cursor: 'pointer',
    fontSize: 12,
};

// ============================================================================
// Custom Node Component
// ============================================================================

/** Map a variable type signature to the closest SocketType for inline editing. */
function variableSocketType(sig: string | undefined): SocketType {
    switch (sig) {
        case 'bool': return 'bool';
        case 'int': return 'int';
        case 'string': return 'string';
        case 'float2': return 'float2';
        case 'float3': return 'float3';
        case 'float4': return 'float4';
        case 'color': return 'color';
        default: return 'float';
    }
}

/** Def sockets merged with any extra dynamically-discovered sockets (def order first). */
function mergeSocketNames(defNames: readonly string[] | undefined, dynNames: string[] | undefined): string[] {
    if (!defNames) return dynNames ?? [];
    if (!dynNames || dynNames.length === 0) return [...defNames];
    return [...defNames, ...dynNames.filter(n => !defNames.includes(n))];
}

function GraphNodeComponent({ id, data, selected }: NodeProps<FlowNodeData>): React.JSX.Element {
    const def = getNodeDef(data.op);
    const dyn = data.dynamicSockets;
    const isSubgraph = data.subgraphIndex !== undefined;
    // Composition (macro) blocks: one logical node backed by an expansion of
    // standard nodes — distinct color + badge so authors know it's not an op.
    const isMacro = !isSubgraph && data.op.startsWith(MACRO_OP_PREFIX);
    const category = isSubgraph ? 'subgraph' : (def?.category ?? (dyn?.flowInputs.length ? 'execution' : 'data'));
    const color = isMacro ? NODE_COLORS.macro : NODE_COLORS[category];
    const flowInputs = mergeSocketNames(def?.flowInputs, dyn?.flowInputs);
    const flowOutputs = mergeSocketNames(def?.flowOutputs, dyn?.flowOutputs);
    const { setNodes } = useReactFlow();
    const { variables } = useEditorContext();
    const allEdges = useEdges();
    const connectedInputs = useMemo(() => {
        const set = new Set<string>();
        for (const e of allEdges) {
            if (e.target === id && e.targetHandle?.startsWith('data-in-')) {
                set.add(e.targetHandle.replace('data-in-', ''));
            }
        }
        return set;
    }, [allEdges, id]);

    const updateConfig = useCallback((fieldName: string, value: unknown) => {
        setNodes(nds => nds.map(n => {
            if (n.id !== id) return n;
            const d = n.data as FlowNodeData;
            return { ...n, data: { ...d, config: { ...d.config, [fieldName]: value } } };
        }));
    }, [id, setNodes]);

    const updateInlineValue = useCallback((socketName: string, value: unknown) => {
        setNodes(nds => nds.map(n => {
            if (n.id !== id) return n;
            const d = n.data as FlowNodeData;
            return { ...n, data: { ...d, inlineValues: { ...d.inlineValues, [socketName]: [value] } } };
        }));
    }, [id, setNodes]);

    return (
        <div style={{
            background: isSubgraph ? '#1e1b2e' : isMacro ? '#2a1a2e' : '#2d2d2d',
            border: `2px solid ${selected ? '#fff' : color}`,
            borderRadius: 6, minWidth: 150, fontSize: 12, fontFamily: 'system-ui, sans-serif',
        }}>
            <div style={{
                background: color, padding: '4px 10px', borderRadius: '4px 4px 0 0',
                color: '#fff', fontWeight: 600, fontSize: 11, textTransform: 'uppercase',
                letterSpacing: '0.05em', display: 'flex', alignItems: 'center', gap: 6,
            }}>
                {isSubgraph && <span style={{ fontSize: 13 }}>{'\u25A3'}</span>}
                {isMacro && <span style={{ fontSize: 13 }}>{MACRO_BADGE}</span>}
                {data.label}
            </div>
            {flowInputs.map((name, i, arr) => (
                <Handle key={`flow-in-${name}`} type="target" position={Position.Top} id={`flow-in-${name}`}
                    style={{ background: '#f59e0b', width: 10, height: 10, top: -5, left: `${((i + 1) / (arr.length + 1)) * 100}%`, borderRadius: '2px' }} />
            ))}
            {flowInputs.length > 1 ? (
                <div style={{ display: 'flex', justifyContent: 'space-around', padding: '4px 10px 0', fontSize: 9, color: '#888' }}>
                    {flowInputs.map(name => <span key={name}>{name}</span>)}
                </div>
            ) : null}
            <div className="nodrag" style={{ padding: '6px 10px', display: 'flex', gap: 16 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                    {def?.dataInputs.map(socket => {
                        // Set State's "state" socket gets a dropdown of the target's
                        // declared states (still connectable for dynamic state names).
                        if (data.op === GraphOps.TRACE_SET_STATE && socket.name === 'state') {
                            return (
                                <StateInputSocket key={socket.name}
                                    targetPath={typeof data.config['target'] === 'string' ? data.config['target'] : ''}
                                    inlineValue={data.inlineValues[socket.name]}
                                    onInlineChange={(val) => updateInlineValue(socket.name, val)}
                                    connected={connectedInputs.has(socket.name)} />
                            );
                        }
                        // For variable set/get nodes, resolve the socket type from the selected variable
                        const isVarNode = data.op === GraphOps.VARIABLE_SET || data.op === GraphOps.VARIABLE_GET;
                        const resolvedSocket = isVarNode && socket.name === 'value'
                            ? { ...socket, type: variableSocketType(ALL_TYPE_SIGNATURES[variables[data.config.variable as number]?.type]) }
                            : socket;
                        return (
                            <DataInputSocket key={socket.name} socket={resolvedSocket} inlineValue={data.inlineValues[socket.name]}
                                onInlineChange={(val) => updateInlineValue(socket.name, val)} connected={connectedInputs.has(socket.name)} />
                        );
                    })}
                    {!def && dyn?.dataInputs.map(name => (
                        <div key={name} style={{ position: 'relative', padding: '2px 0', color: '#aaa', fontSize: 11 }}>
                            <Handle type="target" position={Position.Left} id={`data-in-${name}`}
                                style={{ background: '#64748b', width: 8, height: 8, left: -14, borderRadius: '50%' }} />
                            {name}
                        </div>
                    ))}
                    {def?.configFields.map(field => (
                        <ConfigField key={field.name} field={field} value={data.config[field.name]}
                            onChange={(val) => updateConfig(field.name, val)} op={data.op} allConfig={data.config} />
                    ))}
                    {!def && !isSubgraph && Object.entries(data.config).map(([key, val]) => (
                        <div key={key} style={{ padding: '1px 0', color: '#777', fontSize: 10 }}>{key}: {String(val)}</div>
                    ))}
                    {isSubgraph && <div style={{ padding: '2px 0', color: '#a78bfa', fontSize: 10 }}>Double-click to edit</div>}
                    {isMacro && <div style={{ padding: '2px 0', color: '#e879f9', fontSize: 10 }}>Composition {'·'} expands to standard nodes</div>}
                </div>
                {(def ? def.dataOutputs.map(s => s.name) : dyn?.dataOutputs ?? []).length > 0 && (
                    <div style={{ textAlign: 'right' }}>
                        {(def ? def.dataOutputs.map(s => s.name) : dyn?.dataOutputs ?? []).map(name => (
                            <div key={name} style={{ position: 'relative', padding: '2px 0', color: '#aaa', fontSize: 11 }}>
                                {name}
                                <Handle type="source" position={Position.Right} id={`data-out-${name}`}
                                    style={{ background: '#64748b', width: 8, height: 8, right: -14, borderRadius: '50%' }} />
                            </div>
                        ))}
                    </div>
                )}
            </div>
            {flowOutputs.map((name, i, arr) => (
                <Handle key={`flow-out-${name}`} type="source" position={Position.Bottom} id={`flow-out-${name}`}
                    style={{ background: '#f59e0b', width: 10, height: 10, bottom: -5, left: `${((i + 1) / (arr.length + 1)) * 100}%`, borderRadius: '2px' }} />
            ))}
            {flowOutputs.length > 1 ? (
                <div style={{ display: 'flex', justifyContent: 'space-around', padding: '0 10px 4px', fontSize: 9, color: '#888' }}>
                    {flowOutputs.map(name => <span key={name}>{name}</span>)}
                </div>
            ) : null}
        </div>
    );
}

// ============================================================================
// Data Input Socket
// ============================================================================

function DataInputSocket({ socket, inlineValue, onInlineChange, connected }: {
    socket: SocketDef; inlineValue: unknown[] | undefined; onInlineChange: (val: unknown) => void;
    connected?: boolean;
}): React.JSX.Element {
    const currentVal = inlineValue?.[0];
    return (
        <div style={{ position: 'relative', padding: '2px 0', fontSize: 11 }}>
            <Handle type="target" position={Position.Left} id={`data-in-${socket.name}`}
                style={{ background: '#64748b', width: 8, height: 8, left: -14, borderRadius: '50%' }} />
            {connected ? (
                <span style={{ color: '#aaa' }}>{socket.name}</span>
            ) : socket.type === 'bool' ? (
                <label style={CHECKBOX_LABEL}>
                    <input type="checkbox" checked={currentVal === true} onChange={(e) => onInlineChange(e.target.checked)} style={{ margin: 0 }} />
                    {socket.name}
                </label>
            ) : (socket.type === 'int' || socket.type === 'float') ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <span style={{ color: '#888', fontSize: 10 }}>{socket.name}</span>
                    <input type="number" value={typeof currentVal === 'number' ? currentVal : ''}
                        onChange={(e) => onInlineChange(e.target.value === '' ? undefined : socket.type === 'int' ? Math.round(Number(e.target.value)) : Number(e.target.value))}
                        placeholder="0" style={{ ...INPUT_STYLE, width: 50 }} step={socket.type === 'int' ? '1' : 'any'} />
                </div>
            ) : (socket.type === 'string' || socket.type === 'any') ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <span style={{ color: '#888', fontSize: 10 }}>{socket.name}</span>
                    <input type="text" value={typeof currentVal === 'string' ? currentVal : typeof currentVal === 'number' || typeof currentVal === 'boolean' ? String(currentVal) : ''}
                        onChange={(e) => {
                            const raw = e.target.value;
                            if (raw === '') { onInlineChange(undefined); return; }
                            if (socket.type === 'any') {
                                // Auto-detect type for 'any' sockets
                                if (raw === 'true') { onInlineChange(true); return; }
                                if (raw === 'false') { onInlineChange(false); return; }
                                const num = Number(raw);
                                if (!isNaN(num) && raw.trim() !== '') { onInlineChange(num); return; }
                            }
                            onInlineChange(raw);
                        }}
                        placeholder="" style={{ ...INPUT_STYLE, width: 60 }} />
                </div>
            ) : socket.type === 'color' ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <span style={{ color: '#888', fontSize: 10 }}>{socket.name}</span>
                    <input type="color" value={typeof currentVal === 'string' ? currentVal : '#000000'}
                        onChange={(e) => onInlineChange(e.target.value)}
                        style={{ width: 28, height: 20, padding: 0, border: '1px solid #555', borderRadius: 3, cursor: 'pointer', background: 'none' }} />
                </div>
            ) : (socket.type === 'float2' || socket.type === 'float3' || socket.type === 'float4') ? (
                (() => {
                    const count = socket.type === 'float2' ? 2 : socket.type === 'float3' ? 3 : 4;
                    const labels = count === 2 ? ['X', 'Y'] : count === 3 ? ['X', 'Y', 'Z'] : ['X', 'Y', 'Z', 'W'];
                    const vals = Array.isArray(inlineValue) ? inlineValue : [];
                    return (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <span style={{ color: '#888', fontSize: 10 }}>{socket.name}</span>
                            {Array.from({ length: count }, (_, ci) => (
                                <input key={ci} type="number" step="any"
                                    value={typeof vals[ci] === 'number' ? vals[ci] : ''}
                                    onChange={(e) => {
                                        const newVals = [...vals];
                                        while (newVals.length < count) newVals.push(0);
                                        newVals[ci] = e.target.value === '' ? 0 : Number(e.target.value);
                                        onInlineChange(newVals);
                                    }}
                                    placeholder={labels[ci]}
                                    style={{ ...INPUT_STYLE, width: 36 }} />
                            ))}
                        </div>
                    );
                })()
            ) : (
                <span style={{ color: '#aaa' }}>{socket.name}</span>
            )}
        </div>
    );
}

// ============================================================================
// State Input Socket — Set State's "state" data input
// ============================================================================

/**
 * Dropdown editor for Set State's "state" data-input socket: the target's
 * declared states + Base, a live "→ N objects" scope count from the shared
 * runtime walk (collectScopedStateTargets), and hints for a missing target or
 * a stateless target. The socket handle stays live so a wire can still drive
 * the state name dynamically — when connected, only the socket label shows.
 */
function StateInputSocket({ targetPath, inlineValue, onInlineChange, connected }: {
    targetPath: string; inlineValue: unknown[] | undefined; onInlineChange: (val: unknown) => void;
    connected?: boolean;
}): React.JSX.Element {
    const { contextObject } = useEditorContext();
    // Empty target = "self" (the config default / picker placeholder) → the
    // context object, not unresolved.
    const owner = contextObject
        ? (targetPath ? (contextObject.traceScene.resolveSceneObject(targetPath, contextObject) ?? null) : contextObject)
        : null;
    const stateNames = owner?.getBehavior(Properties)?.getStateNames() ?? [];
    const current = typeof inlineValue?.[0] === 'string' ? inlineValue[0] : '';
    const members = owner ? collectScopedStateTargets(owner, current === '' ? undefined : current).length : 0;
    return (
        <div style={{ position: 'relative', padding: '2px 0', fontSize: 11 }}>
            <Handle type="target" position={Position.Left} id="data-in-state"
                style={{ background: '#64748b', width: 8, height: 8, left: -14, borderRadius: '50%' }} />
            {connected ? (
                <span style={{ color: '#aaa' }}>state</span>
            ) : (<>
                <div style={{ fontSize: 9, color: '#888', marginBottom: 1 }}>state</div>
                <select value={current}
                    onChange={(e) => onInlineChange(e.target.value === '' ? undefined : e.target.value)}
                    style={{ ...INPUT_STYLE, width: '100%' }}>
                    <option value="">Base</option>
                    {stateNames.map(n => <option key={n} value={n}>{n}</option>)}
                    {/* An authored state that no longer exists on the owner stays selectable. */}
                    {current !== '' && !stateNames.includes(current) && <option value={current}>{current} (missing)</option>}
                </select>
                {owner && stateNames.length === 0 && <div style={{ fontSize: 9, color: '#f59e0b', marginTop: 2 }}>target has no states</div>}
                {owner && <div style={{ fontSize: 9, color: '#16a34a', marginTop: 2 }}>→ {members} object{members === 1 ? '' : 's'}</div>}
                {!owner && targetPath && <div style={{ fontSize: 9, color: '#f59e0b', marginTop: 2 }}>target not found</div>}
            </>)}
        </div>
    );
}

// ============================================================================
// Config Field
// ============================================================================

/**
 * Effective state owners: objects that own their (non-empty) state set — group
 * boundaries and self-owned stateful objects. Excludes fan-out-seeded members,
 * whose state names are copies owned by an ancestor group (resolveStateOwner
 * climbs to that group, so they fail the identity check).
 */
function isStateOwner(obj: SceneObject): boolean {
    return (obj.getBehavior(Properties)?.getStateNames().length ?? 0) > 0
        && resolveStateOwner(obj) === obj;
}

function ConfigField({ field, value, onChange, op, allConfig }: {
    field: { name: string; type: string; label: string; default?: unknown };
    value: unknown; onChange: (val: unknown) => void; op: string; allConfig: Record<string, unknown>;
}): React.JSX.Element {
    const { contextObject, variables } = useEditorContext();

    // Set State — a single SCOPED node (it fans out to the whole state group at
    // runtime; STATE_SCOPE_SPEC.md). Authoring niceties only: the target picker
    // lists only state owners (nested by group hierarchy) and snaps the pick to
    // its group owner (so the scope roots at the group, not a member — the
    // runtime never up-snaps). The state itself is a data-input socket, edited
    // by StateInputSocket above, not a config field.
    if (op === GraphOps.TRACE_SET_STATE && contextObject && field.name === 'target') {
        const targetPath = typeof allConfig['target'] === 'string' ? allConfig['target'] : '';
        return (<div style={{ padding: '2px 0' }}>
            <div style={{ fontSize: 9, color: '#888', marginBottom: 1 }}>{field.label}</div>
            <SceneObjectPicker value={targetPath || undefined}
                onChange={(ref: SceneObjectRef) => {
                    // Snap the pick to its group owner so the node targets the group.
                    const picked = ref.getPath()
                        ? contextObject.traceScene.resolveSceneObject(ref.getPath(), contextObject)
                        : null;
                    onChange(picked ? contextObject.traceScene.getRelativePath(resolveStateOwner(picked), contextObject) : (ref.getPath() || ''));
                }}
                placeholder="self" contextObject={contextObject} filter={isStateOwner} />
        </div>);
    }

    if (field.name === 'variable' && (op === GraphOps.VARIABLE_GET || op === GraphOps.VARIABLE_SET)) {
        const currentIndex = typeof value === 'number' ? value : 0;
        return (
            <div style={{ padding: '2px 0' }}>
                <div style={{ fontSize: 9, color: '#888', marginBottom: 1 }}>Variable</div>
                {variables.length > 0 ? (
                    <select value={currentIndex} onChange={(e) => onChange(Number(e.target.value))} style={{ ...INPUT_STYLE, width: '100%' }}>
                        {variables.map((v, i) => <option key={i} value={i}>{v.name} : {ALL_TYPE_SIGNATURES[v.type] ?? 'float'}</option>)}
                    </select>
                ) : <div style={{ fontSize: 10, color: '#f59e0b' }}>No variables defined. Add one in the Variables panel below.</div>}
            </div>
        );
    }
    if (field.type === 'boolean') {
        return (<label style={{ ...CHECKBOX_LABEL, padding: '2px 0' }}>
            <input type="checkbox" checked={value === true} onChange={(e) => onChange(e.target.checked)} style={{ margin: 0 }} />
            <span>{field.label}</span>
        </label>);
    }
    if (field.type === 'number') {
        return (<div style={{ padding: '2px 0' }}>
            <div style={{ fontSize: 9, color: '#888', marginBottom: 1 }}>{field.label}</div>
            <input type="number" value={typeof value === 'number' ? value : ''}
                onChange={(e) => onChange(e.target.value === '' ? undefined : Number(e.target.value))}
                placeholder={typeof field.default === 'string' || typeof field.default === 'number' ? String(field.default) : ''}
                style={INPUT_STYLE} step="any" />
        </div>);
    }
    if (field.type === 'transformPath' && contextObject) {
        return (<div style={{ padding: '2px 0' }}>
            <div style={{ fontSize: 9, color: '#888', marginBottom: 1 }}>{field.label}</div>
            <SceneObjectPicker value={typeof value === 'string' ? value : undefined}
                onChange={(ref: SceneObjectRef) => onChange(ref.getPath() || '')} placeholder="self" contextObject={contextObject} />
        </div>);
    }
    if (field.type === 'transformProperty') {
        const current = typeof value === 'string' ? value : 'translation';
        return (<div style={{ padding: '2px 0' }}>
            <div style={{ fontSize: 9, color: '#888', marginBottom: 1 }}>{field.label}</div>
            <select value={current} onChange={(e) => onChange(e.target.value)} style={{ ...INPUT_STYLE, width: '100%' }}>
                <option value="translation">Position</option>
                <option value="rotation">Rotation</option>
                <option value="scale">Scale</option>
            </select>
        </div>);
    }
    if (field.name === 'clip' && (op === GraphOps.TRACE_PLAY_ANIMATION || op === GraphOps.TRACE_PLAY_ANIMATION_EXTERNAL) && contextObject) {
        const targetPath = typeof allConfig['target'] === 'string' ? allConfig['target'] : '';
        const targetObj = targetPath ? (contextObject.traceScene.resolveSceneObject(targetPath, contextObject) ?? contextObject) : contextObject;
        const mode = op === GraphOps.TRACE_PLAY_ANIMATION_EXTERNAL ? 'custom' as const : 'embedded' as const;
        return (<div style={{ padding: '2px 0' }}>
            <div style={{ fontSize: 9, color: '#888', marginBottom: 1 }}>{field.label}</div>
            <AnimationClipPicker clip={typeof value === 'string' ? value : undefined}
                src={op === GraphOps.TRACE_PLAY_ANIMATION_EXTERNAL ? (typeof allConfig['src'] === 'string' ? allConfig['src'] : undefined) : undefined}
                sceneObject={targetObj} compact restrictMode={mode} onClipChange={(clip) => onChange(clip ?? '')} />
        </div>);
    }
    return (<div style={{ padding: '2px 0' }}>
        <div style={{ fontSize: 9, color: '#888', marginBottom: 1 }}>{field.label}</div>
        <input type="text" value={typeof value === 'string' ? value : ''} onChange={(e) => onChange(e.target.value)} placeholder="" style={INPUT_STYLE} />
    </div>);
}

// ============================================================================
// Port Node Component — represents subgraph interface sockets
// ============================================================================

function PortNodeComponent({ data }: NodeProps<FlowNodeData>): React.JSX.Element {
    const color = NODE_COLORS.port;
    const dyn = data.dynamicSockets;
    const isInput = data.op === '__port_input';
    return (
        <div style={{ background: '#2d1520', border: `2px solid ${color}`, borderRadius: 8, minWidth: 120, fontSize: 12, fontFamily: 'system-ui, sans-serif' }}>
            <div style={{ background: color, padding: '4px 10px', borderRadius: '6px 6px 0 0', color: '#fff', fontWeight: 600, fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.08em', textAlign: 'center' }}>
                {data.label}
            </div>
            {(dyn?.flowOutputs ?? []).map((name, i, arr) => (
                <Handle key={`flow-out-${name}`} type="source" position={Position.Bottom} id={`flow-out-${name}`}
                    style={{ background: '#f59e0b', width: 10, height: 10, bottom: -5, left: `${((i + 1) / (arr.length + 1)) * 100}%`, borderRadius: '2px' }} />
            ))}
            {(dyn?.flowInputs ?? []).map((name, i, arr) => (
                <Handle key={`flow-in-${name}`} type="target" position={Position.Top} id={`flow-in-${name}`}
                    style={{ background: '#f59e0b', width: 10, height: 10, top: -5, left: `${((i + 1) / (arr.length + 1)) * 100}%`, borderRadius: '2px' }} />
            ))}
            <div style={{ padding: '4px 10px' }}>
                {isInput && (dyn?.dataOutputs ?? []).map(name => (
                    <div key={name} style={{ position: 'relative', padding: '2px 0', color: '#fda4af', fontSize: 11, textAlign: 'right' }}>
                        {name}
                        <Handle type="source" position={Position.Right} id={`data-out-${name}`}
                            style={{ background: '#64748b', width: 8, height: 8, right: -14, borderRadius: '50%' }} />
                    </div>
                ))}
                {!isInput && (dyn?.dataInputs ?? []).map(name => (
                    <div key={name} style={{ position: 'relative', padding: '2px 0', color: '#fda4af', fontSize: 11 }}>
                        <Handle type="target" position={Position.Left} id={`data-in-${name}`}
                            style={{ background: '#64748b', width: 8, height: 8, left: -14, borderRadius: '50%' }} />
                        {name}
                    </div>
                ))}
            </div>
        </div>
    );
}

// ============================================================================
// Variables Dock Node — always-present node with one output handle per variable
// ============================================================================

const VARDOCK_ID = '__vardock';

function VariablesDockNode({ selected }: NodeProps<FlowNodeData>): React.JSX.Element {
    const { variables } = useEditorContext();
    return (
        <div style={{
            background: '#1a2a1a', border: `2px solid ${selected ? '#fff' : '#4ade80'}`,
            borderRadius: 6, minWidth: 140, fontSize: 12, fontFamily: 'system-ui, sans-serif',
        }}>
            <div style={{
                background: '#16a34a', padding: '4px 10px', borderRadius: '4px 4px 0 0',
                color: '#fff', fontWeight: 600, fontSize: 10, textTransform: 'uppercase',
                letterSpacing: '0.05em',
            }}>
                Variables
            </div>
            <div className="nodrag" style={{ padding: '6px 10px' }}>
                {variables.length === 0 && (
                    <div style={{ color: '#666', fontSize: 10, fontStyle: 'italic' }}>No variables</div>
                )}
                {variables.map((v, i) => (
                    <div key={i} style={{ position: 'relative', padding: '3px 0', color: '#aaa', fontSize: 11, textAlign: 'right', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 4 }}>
                        <span style={{ color: '#666', fontSize: 9 }}>{ALL_TYPE_SIGNATURES[v.type] ?? '?'}</span>
                        <span>{v.name}</span>
                        <Handle type="source" position={Position.Right} id={`data-out-var-${i}`}
                            style={{ background: '#4ade80', width: 8, height: 8, right: -14, borderRadius: '50%' }} />
                    </div>
                ))}
                <button onClick={() => {
                    // Dispatch custom event to add a variable (handled in GraphView)
                    window.dispatchEvent(new CustomEvent('trace-vardock-add'));
                }} style={{ width: '100%', marginTop: 4, padding: '3px', background: '#333', border: '1px solid #555', borderRadius: 3, color: '#aaa', cursor: 'pointer', fontSize: 10 }}>+ Add</button>
            </div>
        </div>
    );
}

// Suppress the "not a valid connection" when dragging from dock to a flow handle
// (dock only has data outputs)

// ============================================================================
// Node type registry
// ============================================================================

const nodeTypes: NodeTypes = {
    event: GraphNodeComponent,
    execution: GraphNodeComponent,
    data: GraphNodeComponent,
    subgraph: GraphNodeComponent,
    port: PortNodeComponent,
    'variables-dock': VariablesDockNode,
};

// ============================================================================
// Add Node / Templates Menu
// ============================================================================

function AddMenu({ onAddNode, onLoadTemplate }: {
    onAddNode: (op: string, position: { x: number; y: number }) => void;
    onLoadTemplate: (graph: GraphData) => void;
}): React.JSX.Element {
    const { screenToFlowPosition } = useReactFlow();
    const { contextObject: ctx } = useEditorContext();
    const [open, setOpen] = useState(false);
    const [tab, setTab] = useState<'nodes' | 'templates'>('nodes');
    const [search, setSearch] = useState('');
    const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
    const [highlightIndex, setHighlightIndex] = useState(0);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const categories: NodeCategory[] = ['event', 'execution', 'data'];

    const searchLower = search.toLowerCase();
    const isSearching = searchLower.length > 0;

    // Build filtered nodes per category
    const filteredByCategory = categories.map(cat => ({
        cat,
        defs: NODE_DEFS.filter(d => d.category === cat && (!isSearching || d.label.toLowerCase().includes(searchLower))),
    })).filter(g => g.defs.length > 0);

    // Flat list for keyboard navigation
    const flatItems: { type: 'node'; def: NodeDef }[] = [];
    for (const group of filteredByCategory) {
        if (!isSearching && collapsed[group.cat]) continue;
        for (const def of group.defs) {
            flatItems.push({ type: 'node', def });
        }
    }

    const menuRef = useRef<HTMLDivElement>(null);

    // Focus search when opened; reset on close
    useEffect(() => {
        if (open) {
            setSearch('');
            setHighlightIndex(0);
            setCollapsed({});
            requestAnimationFrame(() => searchInputRef.current?.focus());
        }
    }, [open]);

    // Click outside to close
    useEffect(() => {
        if (!open) return;
        const doc = menuRef.current?.ownerDocument ?? document;
        const handle = (e: Event) => {
            if (menuRef.current && !menuRef.current.contains(e.target as HTMLElement)) setOpen(false);
        };
        const tid = setTimeout(() => doc.addEventListener('pointerdown', handle, true), 0);
        return () => { clearTimeout(tid); doc.removeEventListener('pointerdown', handle, true); };
    }, [open]);

    // Clamp highlight
    useEffect(() => {
        setHighlightIndex(prev => Math.min(prev, Math.max(0, flatItems.length - 1)));
    }, [flatItems.length]);

    const handleSelect = (def: NodeDef) => {
        const container = menuRef.current?.closest('.react-flow') as HTMLElement | null;
        const rect = container?.getBoundingClientRect();
        const centerX = rect ? rect.left + rect.width / 2 : window.innerWidth / 2;
        const centerY = rect ? rect.top + rect.height / 2 : window.innerHeight / 2;
        const flowPos = screenToFlowPosition({ x: centerX, y: centerY });
        onAddNode(def.op, { x: flowPos.x + Math.random() * 100 - 50, y: flowPos.y + Math.random() * 100 - 50 });
        setOpen(false);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setHighlightIndex(prev => Math.min(prev + 1, flatItems.length - 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setHighlightIndex(prev => Math.max(prev - 1, 0));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            const item = flatItems[highlightIndex];
            if (item) handleSelect(item.def);
        } else if (e.key === 'Escape') {
            setOpen(false);
        }
    };

    const toggleCategory = (cat: string) => {
        setCollapsed(prev => ({ ...prev, [cat]: !prev[cat] }));
    };

    // Track cumulative index for highlight matching
    let itemIndex = 0;

    return (
        <div ref={menuRef} style={{ position: 'absolute', top: 10, left: 10, zIndex: 10 }}>
            <button onClick={() => setOpen(!open)} style={{ padding: '6px 14px', background: '#333', border: '1px solid #555', borderRadius: 4, color: '#ddd', cursor: 'pointer', fontSize: 13 }}>+ Add</button>
            {open && (
                <div style={{ position: 'absolute', top: 36, left: 0, background: '#2a2a2a', border: '1px solid #555', borderRadius: 6, minWidth: 220, maxHeight: 450, display: 'flex', flexDirection: 'column' }}>
                    <div style={{ display: 'flex', borderBottom: '1px solid #444' }}>
                        {(['nodes', 'templates'] as const).map(t => (
                            <button key={t} onClick={() => setTab(t)} style={{ flex: 1, padding: '6px 8px', background: tab === t ? '#3a3a3a' : 'none', border: 'none', color: tab === t ? '#fff' : '#888', cursor: 'pointer', fontSize: 11, textTransform: 'uppercase', fontWeight: tab === t ? 600 : 400 }}>{t}</button>
                        ))}
                    </div>
                    {tab === 'nodes' ? (
                        <>
                            <div style={{ padding: '6px 6px 4px', borderBottom: '1px solid #3a3a3a' }}>
                                <input
                                    ref={searchInputRef}
                                    type="text"
                                    placeholder="Search nodes..."
                                    value={search}
                                    onChange={e => { setSearch(e.target.value); setHighlightIndex(0); }}
                                    onKeyDown={handleKeyDown}
                                    style={{ width: '100%', padding: '4px 8px', backgroundColor: '#333', border: '1px solid #555', borderRadius: 3, color: '#fff', fontSize: 12, outline: 'none', boxSizing: 'border-box' }}
                                />
                            </div>
                            <div style={{ overflowY: 'auto', flex: 1, padding: 4 }}>
                                {filteredByCategory.length > 0 ? filteredByCategory.map(({ cat, defs }) => {
                                    const isCatCollapsed = !isSearching && collapsed[cat];
                                    return (
                                        <div key={cat}>
                                            <button
                                                onClick={() => toggleCategory(cat)}
                                                style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 4, fontSize: 10, textTransform: 'uppercase', color: NODE_COLORS[cat], fontWeight: 600, padding: '6px 8px 2px', letterSpacing: '0.05em', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left' }}
                                            >
                                                <span style={{ fontSize: 8, transition: 'transform 0.15s', transform: isCatCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)', display: 'inline-block' }}>{'\u25BC'}</span>
                                                {cat} ({defs.length})
                                            </button>
                                            {!isCatCollapsed && defs.map(def => {
                                                const myIndex = itemIndex++;
                                                return (
                                                    <button key={def.op} onClick={() => handleSelect(def)}
                                                        style={{ display: 'block', width: '100%', padding: '4px 8px 4px 20px', background: myIndex === highlightIndex ? '#3a3a3a' : 'none', border: 'none', color: '#ccc', cursor: 'pointer', textAlign: 'left', fontSize: 12, borderRadius: 3 }}
                                                        onMouseEnter={() => setHighlightIndex(myIndex)}>
                                                        {def.label}
                                                        {def.registration === 'macro' && <span style={{ color: NODE_COLORS.macro, marginLeft: 5 }} title="Composition — one block backed by standard nodes. It stays one block unless you expand or rewire it.">{MACRO_BADGE}</span>}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    );
                                }) : (
                                    <div style={{ padding: '8px 12px', color: '#666', fontSize: 12 }}>No matches</div>
                                )}
                            </div>
                        </>
                    ) : (
                        <div style={{ padding: 4, overflowY: 'auto', flex: 1 }}>
                            {/*
                              * Samples are starting points, not blocks: they
                              * stamp plain nodes you then edit. The distinction
                              * from a macro (which keeps its identity) is the
                              * one thing an author needs to know here.
                              */}
                            <div style={{ padding: '4px 8px 6px', color: '#777', fontSize: 10, lineHeight: 1.4 }}>
                                Starting points — these place ordinary nodes for you to edit. Unlike a
                                composition <span style={{ color: NODE_COLORS.macro }}>{MACRO_BADGE}</span>, a
                                sample does not stay grouped.
                            </div>
                            {SAMPLE_GRAPHS.map(sample => (
                                <button key={sample.name} onClick={() => { if (ctx) { onLoadTemplate(sample.build(ctx)); setOpen(false); } }}
                                    style={{ display: 'block', width: '100%', padding: '6px 8px', background: 'none', border: 'none', color: '#ccc', cursor: 'pointer', textAlign: 'left', fontSize: 12, borderRadius: 3 }}
                                    onMouseOver={e => (e.currentTarget.style.background = '#3a3a3a')} onMouseOut={e => (e.currentTarget.style.background = 'none')}>
                                    <div style={{ fontWeight: 500 }}>{sample.name}</div>
                                    <div style={{ fontSize: 10, color: '#888' }}>{sample.description}</div>
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

// ============================================================================
// Connect-Drop Menu (appears when dragging a connection into empty space)
// ============================================================================

function ConnectDropMenu({ screenX, screenY, isFlow, sourceHandleType, onSelect, onClose }: {
    screenX: number; screenY: number;
    isFlow: boolean; sourceHandleType: 'source' | 'target';
    onSelect: (def: NodeDef) => void; onClose: () => void;
}): React.JSX.Element {
    const [search, setSearch] = useState('');
    const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
    const [highlightIndex, setHighlightIndex] = useState(0);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);
    const categories: NodeCategory[] = ['event', 'execution', 'data'];

    const searchLower = search.toLowerCase();
    const isSearching = searchLower.length > 0;

    // Filter to nodes that have a compatible handle
    const compatibleDefs = NODE_DEFS.filter(def => {
        if (sourceHandleType === 'source') {
            // Dragged from output → need a node with a matching input
            return isFlow ? def.flowInputs.length > 0 : def.dataInputs.length > 0;
        } else {
            // Dragged from input → need a node with a matching output
            return isFlow ? def.flowOutputs.length > 0 : def.dataOutputs.length > 0;
        }
    });

    const filteredByCategory = categories.map(cat => ({
        cat,
        defs: compatibleDefs.filter(d => d.category === cat && (!isSearching || d.label.toLowerCase().includes(searchLower))),
    })).filter(g => g.defs.length > 0);

    // Flat list for keyboard navigation
    const flatItems: NodeDef[] = [];
    for (const group of filteredByCategory) {
        if (!isSearching && collapsed[group.cat]) continue;
        for (const def of group.defs) flatItems.push(def);
    }

    useEffect(() => {
        requestAnimationFrame(() => searchInputRef.current?.focus());
    }, []);

    useEffect(() => {
        setHighlightIndex(prev => Math.min(prev, Math.max(0, flatItems.length - 1)));
    }, [flatItems.length]);

    // Click outside to close
    useEffect(() => {
        const doc = menuRef.current?.ownerDocument ?? document;
        const handle = (e: Event) => {
            if (menuRef.current && !menuRef.current.contains(e.target as HTMLElement)) onClose();
        };
        const tid = setTimeout(() => doc.addEventListener('pointerdown', handle, true), 0);
        return () => { clearTimeout(tid); doc.removeEventListener('pointerdown', handle, true); };
    }, [onClose]);

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'ArrowDown') { e.preventDefault(); setHighlightIndex(prev => Math.min(prev + 1, flatItems.length - 1)); }
        else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlightIndex(prev => Math.max(prev - 1, 0)); }
        else if (e.key === 'Enter') { e.preventDefault(); if (flatItems[highlightIndex]) onSelect(flatItems[highlightIndex]); }
        else if (e.key === 'Escape') { onClose(); }
    };

    // Position the menu so it stays within the viewport
    const menuWidth = 220;
    const menuHeight = 350;
    const left = screenX + menuWidth > window.innerWidth ? screenX - menuWidth : screenX;
    const top = screenY + menuHeight > window.innerHeight ? Math.max(0, screenY - menuHeight) : screenY;

    let itemIndex = 0;

    return (
        <div ref={menuRef} style={{ position: 'fixed', top, left, width: menuWidth, maxHeight: menuHeight, background: '#2a2a2a', border: '1px solid #555', borderRadius: 6, zIndex: 1000, boxShadow: '0 4px 16px rgba(0,0,0,0.5)', display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '6px 6px 4px', borderBottom: '1px solid #3a3a3a' }}>
                <input
                    ref={searchInputRef}
                    type="text"
                    placeholder="Search nodes..."
                    value={search}
                    onChange={e => { setSearch(e.target.value); setHighlightIndex(0); }}
                    onKeyDown={handleKeyDown}
                    style={{ width: '100%', padding: '4px 8px', backgroundColor: '#333', border: '1px solid #555', borderRadius: 3, color: '#fff', fontSize: 12, outline: 'none', boxSizing: 'border-box' }}
                />
            </div>
            <div style={{ overflowY: 'auto', flex: 1, padding: 4 }}>
                {filteredByCategory.length > 0 ? filteredByCategory.map(({ cat, defs }) => {
                    const isCatCollapsed = !isSearching && collapsed[cat];
                    return (
                        <div key={cat}>
                            <button
                                onClick={() => setCollapsed(prev => ({ ...prev, [cat]: !prev[cat] }))}
                                style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 4, fontSize: 10, textTransform: 'uppercase', color: NODE_COLORS[cat], fontWeight: 600, padding: '6px 8px 2px', letterSpacing: '0.05em', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left' }}
                            >
                                <span style={{ fontSize: 8, transition: 'transform 0.15s', transform: isCatCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)', display: 'inline-block' }}>{'\u25BC'}</span>
                                {cat} ({defs.length})
                            </button>
                            {!isCatCollapsed && defs.map(def => {
                                const myIndex = itemIndex++;
                                return (
                                    <button key={def.op} onClick={() => onSelect(def)}
                                        style={{ display: 'block', width: '100%', padding: '4px 8px 4px 20px', background: myIndex === highlightIndex ? '#3a3a3a' : 'none', border: 'none', color: '#ccc', cursor: 'pointer', textAlign: 'left', fontSize: 12, borderRadius: 3 }}
                                        onMouseEnter={() => setHighlightIndex(myIndex)}>
                                        {def.label}
                                        {def.registration === 'macro' && <span style={{ color: NODE_COLORS.macro, marginLeft: 5 }} title="Composition — one block backed by standard nodes. It stays one block unless you expand or rewire it.">{MACRO_BADGE}</span>}
                                    </button>
                                );
                            })}
                        </div>
                    );
                }) : (
                    <div style={{ padding: '8px 12px', color: '#666', fontSize: 12 }}>No matches</div>
                )}
            </div>
        </div>
    );
}

// ============================================================================
// Variables Panel
// ============================================================================

function VariableValueEditor({ variable, onChange }: { variable: GraphVariable; onChange: (v: GraphVariable) => void }): React.JSX.Element {
    const sig = ALL_TYPE_SIGNATURES[variable.type] ?? 'float';
    const count = TYPE_COMPONENT_COUNT[sig] ?? 1;

    if (sig === 'bool') {
        return (
            <input type="checkbox" checked={!!variable.value[0]}
                onChange={(e) => onChange({ ...variable, value: [e.target.checked ? 1 : 0] })}
                style={{ margin: 0 }} title="Initial value" />
        );
    }
    if (sig === 'color') {
        return (
            <input type="color" value={typeof variable.value[0] === 'string' ? variable.value[0] : '#000000'}
                onChange={(e) => onChange({ ...variable, value: [e.target.value] })}
                style={{ width: 28, height: 20, padding: 0, border: '1px solid #555', borderRadius: 3, cursor: 'pointer', background: 'none' }} />
        );
    }
    if (sig === 'string') {
        return (
            <input type="text" value={typeof variable.value[0] === 'string' ? variable.value[0] : ''}
                onChange={(e) => onChange({ ...variable, value: [e.target.value] })}
                style={{ ...INPUT_STYLE, width: 60 }} placeholder="text" />
        );
    }
    // Numeric: 1–16 component inputs
    const labels = count === 2 ? ['X', 'Y'] : count === 3 ? ['X', 'Y', 'Z'] : count === 4 ? ['X', 'Y', 'Z', 'W'] : undefined;
    return (
        <div style={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            {Array.from({ length: count }, (_, ci) => (
                <input key={ci} type="number" step="any"
                    value={typeof variable.value[ci] === 'number' ? variable.value[ci] : 0}
                    onChange={(e) => {
                        const newValue = [...variable.value];
                        newValue[ci] = Number(e.target.value) || 0;
                        onChange({ ...variable, value: newValue });
                    }}
                    title={labels?.[ci]}
                    placeholder={labels?.[ci] ?? String(ci)}
                    style={{ ...INPUT_STYLE, width: count === 1 ? 50 : 36 }} />
            ))}
        </div>
    );
}

function VariablesPanel({ variables, onChange }: {
    variables: GraphVariable[];
    onChange: (vars: GraphVariable[]) => void;
}): React.JSX.Element {
    const [open, setOpen] = useState(false);

    const updateVar = (i: number, updated: GraphVariable): void => {
        onChange(variables.map((vv, ii) => ii === i ? updated : vv));
    };

    const changeType = (i: number, newTypeIndex: number): void => {
        const sig = ALL_TYPE_SIGNATURES[newTypeIndex] ?? 'float';
        const count = TYPE_COMPONENT_COUNT[sig] ?? 1;
        const defaultValue = sig === 'bool' ? [0] : sig === 'color' ? ['#000000'] : sig === 'string' ? [''] : Array(count).fill(0) as number[];
        onChange(variables.map((vv, ii) => ii === i ? { ...vv, type: newTypeIndex, value: defaultValue } : vv));
    };

    return (
        <div style={{ position: 'absolute', bottom: 10, left: 10, zIndex: 10, background: '#2a2a2a', border: '1px solid #555', borderRadius: 6, minWidth: 240, maxWidth: 400 }}>
            <button onClick={() => setOpen(!open)} style={{ width: '100%', padding: '6px 10px', background: 'none', border: 'none', color: '#ccc', cursor: 'pointer', fontSize: 12, textAlign: 'left', fontWeight: 500 }}>
                Variables ({variables.length}) {open ? '\u25B2' : '\u25BC'}
            </button>
            {open && (
                <div style={{ padding: '0 8px 8px' }}>
                    {variables.map((v, i) => (
                        <div key={i} style={{ display: 'flex', gap: 4, alignItems: 'center', marginBottom: 4, flexWrap: 'wrap' }}>
                            <input type="text" value={v.name} onChange={(e) => updateVar(i, { ...v, name: e.target.value })} style={{ ...INPUT_STYLE, flex: 1, minWidth: 60 }} placeholder="name" />
                            <select value={v.type} onChange={(e) => changeType(i, Number(e.target.value))} style={{ ...INPUT_STYLE, width: 'auto', minWidth: 55 }}>
                                {ALL_TYPE_SIGNATURES.map((sig, si) => <option key={sig} value={si}>{sig}</option>)}
                            </select>
                            <VariableValueEditor variable={v} onChange={(updated) => updateVar(i, updated)} />
                            <button onClick={() => onChange(variables.filter((_, ii) => ii !== i))} style={{ background: 'none', border: 'none', color: '#888', cursor: 'pointer', fontSize: 14, padding: '0 2px' }} title="Remove variable">x</button>
                        </div>
                    ))}
                    <button onClick={() => onChange([...variables, { name: `var${variables.length}`, type: ALL_TYPE_SIGNATURES.indexOf('float'), value: [0] }])}
                        style={{ width: '100%', padding: '4px', background: '#333', border: '1px solid #555', borderRadius: 3, color: '#aaa', cursor: 'pointer', fontSize: 11 }}>+ Add Variable</button>
                </div>
            )}
        </div>
    );
}

// ============================================================================
// Breadcrumb
// ============================================================================

function Breadcrumb({ path, onNavigate }: { path: Array<{ label: string; graphIndex: number }>; onNavigate: (graphIndex: number, depth: number) => void }): React.JSX.Element | null {
    if (path.length <= 1) return null;
    return (
        <div style={{ position: 'absolute', top: 10, left: '50%', transform: 'translateX(-50%)', zIndex: 10, display: 'flex', alignItems: 'center', gap: 4, background: '#2a2a2a', border: '1px solid #555', borderRadius: 4, padding: '4px 10px', fontSize: 12 }}>
            {path.map((entry, i) => (
                <React.Fragment key={i}>
                    {i > 0 && <span style={{ color: '#555' }}>{'\u203A'}</span>}
                    <button onClick={() => onNavigate(entry.graphIndex, i)}
                        style={{ background: 'none', border: 'none', color: i === path.length - 1 ? '#fff' : '#888', cursor: i === path.length - 1 ? 'default' : 'pointer', fontSize: 12, fontWeight: i === path.length - 1 ? 600 : 400, padding: '2px 4px', textDecoration: i === path.length - 1 ? 'none' : 'underline' }}
                        disabled={i === path.length - 1}>{entry.label}</button>
                </React.Fragment>
            ))}
        </div>
    );
}

// ============================================================================
// Port node generation
// ============================================================================

function generatePortNodesAndEdges(
    graph: GraphData,
    existingNodes: Node<FlowNodeData>[],
): { portNodes: Node<FlowNodeData>[]; portEdges: Edge[] } {
    const portNodes: Node<FlowNodeData>[] = [];
    const portEdges: Edge[] = [];

    let minY = Infinity, maxY = -Infinity, avgX = 0;
    for (const n of existingNodes) {
        if (n.position.y < minY) minY = n.position.y;
        if (n.position.y > maxY) maxY = n.position.y;
        avgX += n.position.x;
    }
    avgX = existingNodes.length > 0 ? avgX / existingNodes.length : 200;
    if (minY === Infinity) { minY = 100; maxY = 300; }

    const hasInputFlow = graph.inputFlowSockets && Object.keys(graph.inputFlowSockets).length > 0;
    const hasInputValues = graph.inputValueSockets && Object.keys(graph.inputValueSockets).length > 0;
    if (hasInputFlow || hasInputValues) {
        portNodes.push({
            id: '__port-input', type: 'port', position: { x: avgX - 60, y: minY - 120 }, draggable: true,
            data: { op: '__port_input', label: 'Inputs', config: {}, inlineValues: {},
                dynamicSockets: { flowInputs: [], flowOutputs: graph.inputFlowSockets ? Object.keys(graph.inputFlowSockets) : [], dataInputs: [], dataOutputs: graph.inputValueSockets ? Object.keys(graph.inputValueSockets) : [] } },
        });

        // Auto-connect input flow ports to internal nodes
        if (graph.inputFlowSockets) {
            for (const [name, ref] of Object.entries(graph.inputFlowSockets)) {
                portEdges.push({
                    id: `__port-flow-in-${name}`,
                    source: '__port-input',
                    sourceHandle: `flow-out-${name}`,
                    target: `node-${ref.node}`,
                    targetHandle: `flow-in-${ref.socket}`,
                    animated: true,
                    style: { stroke: '#e11d48', strokeWidth: 2, strokeDasharray: '4 4' },
                });
            }
        }
        // Auto-connect input value ports to internal nodes
        // Value inputs don't have a specific target node in the interface —
        // they're bridged via SubgraphBlock at runtime. Shown as unconnected handles.
    }

    const hasOutputFlow = graph.outputFlowSockets && Object.keys(graph.outputFlowSockets).length > 0;
    const hasOutputValues = graph.outputValueSockets && Object.keys(graph.outputValueSockets).length > 0;
    if (hasOutputFlow || hasOutputValues) {
        portNodes.push({
            id: '__port-output', type: 'port', position: { x: avgX - 60, y: maxY + 120 }, draggable: true,
            data: { op: '__port_output', label: 'Outputs', config: {}, inlineValues: {},
                dynamicSockets: { flowInputs: graph.outputFlowSockets ? Object.keys(graph.outputFlowSockets) : [], flowOutputs: [], dataInputs: graph.outputValueSockets ? Object.keys(graph.outputValueSockets) : [], dataOutputs: [] } },
        });

        // Auto-connect output flow ports from internal nodes
        if (graph.outputFlowSockets) {
            for (const [name, ref] of Object.entries(graph.outputFlowSockets)) {
                portEdges.push({
                    id: `__port-flow-out-${name}`,
                    source: `node-${ref.node}`,
                    sourceHandle: `flow-out-${ref.socket}`,
                    target: '__port-output',
                    targetHandle: `flow-in-${name}`,
                    animated: true,
                    style: { stroke: '#e11d48', strokeWidth: 2, strokeDasharray: '4 4' },
                });
            }
        }
        // Auto-connect output value ports from internal nodes
        if (graph.outputValueSockets) {
            for (const [name, ref] of Object.entries(graph.outputValueSockets)) {
                portEdges.push({
                    id: `__port-value-out-${name}`,
                    source: `node-${ref.node}`,
                    sourceHandle: `data-out-${ref.socket}`,
                    target: '__port-output',
                    targetHandle: `data-in-${name}`,
                    style: { stroke: '#e11d48', strokeWidth: 1.5, strokeDasharray: '4 4' },
                });
            }
        }
    }

    return { portNodes, portEdges };
}

// ============================================================================
// GraphView — inner component that manages ReactFlow state for ONE graph.
// Remounts via key when the view changes, guaranteeing fresh state.
// ============================================================================

interface GraphViewProps {
    graph: GraphData;
    allGraphs: GraphData[];
    navStack: Array<{ label: string; graphIndex: number }>;
    isSubgraphView: boolean;
    onSaveGraph: (nodes: Node<FlowNodeData>[], edges: Edge[], variables: GraphVariable[], portEdges?: Edge[]) => void;
    /** Save current state + navigate into a subgraph in one atomic operation */
    onNavigateIn: (currentNodes: Node<FlowNodeData>[], currentEdges: Edge[], currentVars: GraphVariable[], subgraphIndex: number, label: string) => void;
    /** Save current state + navigate out via breadcrumb */
    onNavigateOut: (currentNodes: Node<FlowNodeData>[], currentEdges: Edge[], currentVars: GraphVariable[], depth: number, portEdges: Edge[]) => void;
    onGroup: (nodes: Node<FlowNodeData>[], edges: Edge[], selectedIds: Set<string>, name: string) => void;
    onInline: (nodes: Node<FlowNodeData>[], edges: Edge[], nodeId: string, variables: GraphVariable[]) => { nodes: Node<FlowNodeData>[]; edges: Edge[]; nextNodeId: number; variables: GraphVariable[] } | null;
    contextObject: SceneObject | null;
}

function GraphView({ graph, allGraphs, navStack, isSubgraphView, onSaveGraph, onNavigateIn, onNavigateOut, onGroup, onInline, contextObject }: GraphViewProps): React.JSX.Element {
    const { nodes: initNodes, edges: initEdges } = useMemo(() => {
        const result = graphToReactFlow(graph, allGraphs);
        if (isSubgraphView) {
            const { portNodes, portEdges } = generatePortNodesAndEdges(graph, result.nodes);
            return { nodes: [...result.nodes, ...portNodes], edges: [...result.edges, ...portEdges] };
        }
        return result;
    }, [graph, allGraphs, isSubgraphView]);

    const initVars = useMemo(() =>
        graph.variables && graph.types
            ? remapVariablesToCanonical(graph.variables, graph.types)
            : graph.variables ?? []
    , [graph]);

    // Add variables dock node to initial nodes
    const initNodesWithDock = useMemo(() => {
        const dockNode: Node<FlowNodeData> = {
            id: VARDOCK_ID, type: 'variables-dock',
            position: { x: -300, y: 0 },
            data: { op: '__vardock', label: 'Variables', config: {}, inlineValues: {}, graphIndex: undefined },
        };
        return [...initNodes, dockNode];
    }, [initNodes]);

    const [nodes, setNodes, onNodesChange] = useNodesState(initNodesWithDock);
    const [edges, setEdges, onEdgesChange] = useEdgesState(initEdges);
    const [variables, setVariables] = useState(initVars);
    const [dirty, setDirty] = useState(false);
    const nodeIdCounter = useRef(
        initNodes.length > 0
            ? Math.max(...initNodes.map(n => { const num = parseInt(n.id.replace(/^node-(?:sg-)?/, ''), 10); return isNaN(num) ? 0 : num; })) + 1
            : 0
    );

    // Keep dock node in sync with variables (forces handle re-render)
    useEffect(() => {
        setNodes(nds => nds.map(n =>
            n.id === VARDOCK_ID
                ? { ...n, data: { ...n.data, config: { _varCount: variables.length } } }
                : n
        ));
    }, [variables, setNodes]);

    // Listen for "add variable" from dock node button
    useEffect(() => {
        const handler = (): void => {
            setVariables(prev => [...prev, { name: `var${prev.length}`, type: ALL_TYPE_SIGNATURES.indexOf('float'), value: [0] }]);
            setDirty(true);
        };
        window.addEventListener('trace-vardock-add', handler);
        return () => window.removeEventListener('trace-vardock-add', handler);
    }, []);

    // Pending group state
    const [pendingGroup, setPendingGroup] = useState<{ ids: Set<string>; snapshotNodes: Node<FlowNodeData>[]; snapshotEdges: Edge[] } | null>(null);
    const [groupName, setGroupName] = useState('subgraph');

    const isValidConnection = useCallback((connection: Connection) => {
        const sourceIsFlow = connection.sourceHandle?.startsWith('flow-') ?? false;
        const targetIsFlow = connection.targetHandle?.startsWith('flow-') ?? false;
        return sourceIsFlow === targetIsFlow;
    }, []);

    const onConnect: OnConnect = useCallback((params: Connection) => {
        // Clear the connect-start ref so onConnectEnd won't show the drop menu
        connectStartRef.current = null;

        // Intercept connections from the variables dock → spawn a variable/get node + connect it
        if (params.source === VARDOCK_ID && params.sourceHandle?.startsWith('data-out-var-')) {
            const varIndex = parseInt(params.sourceHandle.replace('data-out-var-', ''), 10);
            const def = getNodeDef(GraphOps.VARIABLE_GET);
            if (!def || !params.target || !params.targetHandle) return;

            // Find target node position to place getter nearby
            const targetNode = nodes.find(n => n.id === params.target);
            const pos = targetNode
                ? { x: targetNode.position.x - 180, y: targetNode.position.y }
                : { x: 0, y: 0 };

            const id = `node-${nodeIdCounter.current++}`;
            setNodes(nds => [...nds, {
                id, type: def.category, position: pos,
                data: { op: GraphOps.VARIABLE_GET, label: def.label, config: { variable: varIndex }, inlineValues: {} },
            }]);
            setEdges(eds => addEdge({
                source: id, sourceHandle: 'data-out-value',
                target: params.target, targetHandle: params.targetHandle,
                style: { stroke: '#64748b', strokeWidth: 1.5, strokeDasharray: '5 3' },
                interactionWidth: 20,
            }, eds));
            setDirty(true);
            return;
        }

        setEdges(eds => addEdge({
            ...params,
            animated: params.sourceHandle?.startsWith('flow-') ?? false,
            style: params.sourceHandle?.startsWith('flow-') ? { stroke: '#f59e0b', strokeWidth: 2 } : { stroke: '#64748b', strokeWidth: 1.5, strokeDasharray: '5 3' },
            interactionWidth: 20,
        }, eds));
        setDirty(true);
    }, [setEdges, setNodes, nodes]);

    const handleAddNode = useCallback((op: string, position: { x: number; y: number }): string => {
        const def = getNodeDef(op);
        if (!def) return '';
        const id = `node-${nodeIdCounter.current++}`;
        setNodes(nds => [...nds, { id, type: def.category, position,
            data: { op, label: def.label, config: Object.fromEntries(def.configFields.map(f => [f.name, f.default ?? ''])), inlineValues: defaultInlineValues(def) } }]);
        setDirty(true);
        return id;
    }, [setNodes]);

    const { screenToFlowPosition } = useReactFlow();

    // ── Connect-drop: show add-node popup when dragging a connection into empty space ──
    const connectStartRef = useRef<{ nodeId: string; handleId: string; handleType: 'source' | 'target' } | null>(null);
    const [connectDropMenu, setConnectDropMenu] = useState<{ screenX: number; screenY: number; flowX: number; flowY: number; isFlow: boolean; sourceHandleType: 'source' | 'target' } | null>(null);

    const onConnectStart = useCallback((_event: React.MouseEvent | React.TouchEvent, params: { nodeId: string | null; handleId: string | null; handleType: 'source' | 'target' | null }) => {
        if (params.nodeId && params.handleId && params.handleType) {
            connectStartRef.current = { nodeId: params.nodeId, handleId: params.handleId, handleType: params.handleType };
        }
    }, []);

    const onConnectEnd = useCallback((event: MouseEvent | TouchEvent) => {
        // Only show menu if we didn't connect to a valid target (i.e. dropped in empty space)
        const target = event.target as HTMLElement;
        // If we dropped onto a handle or a node, ReactFlow's onConnect handles it
        if (target.closest('.react-flow__handle') || !connectStartRef.current) {
            connectStartRef.current = null;
            return;
        }
        const clientX = 'changedTouches' in event ? event.changedTouches[0].clientX : event.clientX;
        const clientY = 'changedTouches' in event ? event.changedTouches[0].clientY : event.clientY;
        const flowPos = screenToFlowPosition({ x: clientX, y: clientY });
        const start = connectStartRef.current;
        const isFlow = start.handleId.startsWith('flow-');
        setConnectDropMenu({ screenX: clientX, screenY: clientY, flowX: flowPos.x, flowY: flowPos.y, isFlow, sourceHandleType: start.handleType });
    }, [screenToFlowPosition]);

    const handleConnectDropSelect = useCallback((def: NodeDef) => {
        if (!connectDropMenu || !connectStartRef.current) return;
        const start = connectStartRef.current;
        const newNodeId = handleAddNode(def.op, { x: connectDropMenu.flowX, y: connectDropMenu.flowY });
        if (!newNodeId) return;

        // Determine which handle on the new node to connect to
        const isFlow = connectDropMenu.isFlow;
        if (start.handleType === 'source') {
            // Dragged from an output → connect to the new node's first input
            const targetHandle = isFlow
                ? (def.flowInputs[0] ? `flow-in-${def.flowInputs[0]}` : null)
                : (def.dataInputs[0] ? `data-in-${def.dataInputs[0].name}` : null);
            if (targetHandle) {
                setEdges(eds => addEdge({
                    source: start.nodeId, sourceHandle: start.handleId,
                    target: newNodeId, targetHandle,
                    animated: isFlow,
                    style: isFlow ? { stroke: '#f59e0b', strokeWidth: 2 } : { stroke: '#64748b', strokeWidth: 1.5, strokeDasharray: '5 3' },
                    interactionWidth: 20,
                }, eds));
            }
        } else {
            // Dragged from an input → connect the new node's first output to it
            const sourceHandle = isFlow
                ? (def.flowOutputs[0] ? `flow-out-${def.flowOutputs[0]}` : null)
                : (def.dataOutputs[0] ? `data-out-${def.dataOutputs[0].name}` : null);
            if (sourceHandle) {
                setEdges(eds => addEdge({
                    source: newNodeId, sourceHandle,
                    target: start.nodeId, targetHandle: start.handleId,
                    animated: isFlow,
                    style: isFlow ? { stroke: '#f59e0b', strokeWidth: 2 } : { stroke: '#64748b', strokeWidth: 1.5, strokeDasharray: '5 3' },
                    interactionWidth: 20,
                }, eds));
            }
        }
        setDirty(true);
        setConnectDropMenu(null);
        connectStartRef.current = null;
    }, [connectDropMenu, handleAddNode, setEdges]);

    const handleLoadTemplate = useCallback((tplGraph: GraphData) => {
        const { nodes: newNodes, edges: newEdges } = graphToReactFlow(tplGraph);
        const offset = nodeIdCounter.current;
        setNodes(nds => [...nds, ...newNodes.map((n, i) => ({ ...n, id: `node-${offset + i}` }))]);
        setEdges(eds => [...eds, ...newEdges.map(e => ({ ...e, id: `${e.id}-t${offset}`, source: `node-${offset + parseInt(e.source.replace('node-', ''), 10)}`, target: `node-${offset + parseInt(e.target.replace('node-', ''), 10)}` }))]);
        nodeIdCounter.current = offset + newNodes.length;
        setDirty(true);
    }, [setNodes, setEdges]);

    const isInternalNode = (id: string): boolean => id.startsWith('__port-') || id === VARDOCK_ID;
    const graphNodes = useCallback(() => nodes.filter(n => !isInternalNode(n.id)), [nodes]);
    const graphEdges = useCallback(() => edges.filter(e => !isInternalNode(e.source) && !isInternalNode(e.target)), [edges]);
    const portEdges = useCallback(() => edges.filter(e => e.source.startsWith('__port-') || e.target.startsWith('__port-')), [edges]);

    const handleSave = useCallback(() => {
        onSaveGraph(graphNodes(), graphEdges(), variables, portEdges());
        setDirty(false);
    }, [graphNodes, graphEdges, portEdges, variables, onSaveGraph]);

    const handleNodeDoubleClick = useCallback((_event: React.MouseEvent, node: Node<FlowNodeData>) => {
        if (node.data.subgraphIndex === undefined) return;
        onNavigateIn(graphNodes(), graphEdges(), variables, node.data.subgraphIndex, node.data.subgraphName ?? node.data.op);
    }, [graphNodes, graphEdges, variables, onNavigateIn]);

    const handleBreadcrumb = useCallback((_graphIndex: number, depth: number) => {
        onNavigateOut(graphNodes(), graphEdges(), variables, depth, portEdges());
    }, [graphNodes, graphEdges, portEdges, variables, onNavigateOut]);

    const handleGroupStart = useCallback(() => {
        const selectedIds = new Set(nodes.filter(n => n.selected).map(n => n.id));
        if (selectedIds.size < 2) return;
        setPendingGroup({ ids: selectedIds, snapshotNodes: nodes, snapshotEdges: edges });
        setGroupName('subgraph');
    }, [nodes, edges]);

    const handleGroupConfirm = useCallback(() => {
        if (!pendingGroup || !groupName.trim()) return;
        onGroup(pendingGroup.snapshotNodes, pendingGroup.snapshotEdges, pendingGroup.ids, groupName.trim());
        setPendingGroup(null);
    }, [pendingGroup, groupName, onGroup]);

    const handleInline = useCallback((nodeId: string) => {
        const result = onInline(nodes, edges, nodeId, variables);
        if (result) {
            setNodes(result.nodes);
            setEdges(result.edges);
            // The body's variables are adopted into this graph on inline — its
            // scope is gone, so they have to live somewhere.
            setVariables(result.variables);
            nodeIdCounter.current = result.nextNodeId;
            setDirty(true);
        }
    }, [nodes, edges, variables, onInline, setNodes, setEdges]);

    const handleKeyDown = useCallback((event: React.KeyboardEvent) => {
        if (event.key === 'Backspace' || event.key === 'Delete') {
            const target = event.target as HTMLElement;
            if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT') return;
            const sn = nodes.filter(n => n.selected && !isInternalNode(n.id));
            const se = edges.filter(e => e.selected);
            if (sn.length > 0 || se.length > 0) {
                event.preventDefault();
                if (sn.length > 0) onNodesChange(sn.map(n => ({ type: 'remove' as const, id: n.id })));
                if (se.length > 0) onEdgesChange(se.map(e => ({ type: 'remove' as const, id: e.id })));
                setDirty(true);
            }
        }
    }, [nodes, edges, onNodesChange, onEdgesChange]);

    // Expand a collapsed macro into its raw member nodes. ONE-WAY: the members
    // are emitted untagged on save, so the instance permanently degrades to
    // plain nodes (degrade-by-design) — no re-collapse is offered.
    const handleExpandMacro = useCallback((nodeId: string) => {
        // The expansion may own private variables (Interval's `running`
        // flag) and events; variables join the editor's list, events are
        // materialized on save from the indices the send/receive nodes use.
        const result = expandMacroNode(nodes, edges, nodeId, nodeIdCounter.current, {
            variables,
            eventCount: graph.events?.length ?? 0,
        });
        if (result) {
            setNodes(result.nodes);
            setEdges(result.edges);
            setVariables(result.variables);
            nodeIdCounter.current = result.nextNodeId;
            setDirty(true);
        }
    }, [nodes, edges, variables, graph, setNodes, setEdges]);

    const selectedCount = useMemo(() => nodes.filter(n => n.selected).length, [nodes]);
    const singleSelectedSubgraph = useMemo(() => {
        const selected = nodes.filter(n => n.selected);
        return selected.length === 1 && selected[0].data.subgraphIndex !== undefined ? selected[0].id : null;
    }, [nodes]);
    const singleSelectedMacro = useMemo(() => {
        const selected = nodes.filter(n => n.selected);
        return selected.length === 1 && selected[0].data.subgraphIndex === undefined
            && selected[0].data.op.startsWith(MACRO_OP_PREFIX) ? selected[0].id : null;
    }, [nodes]);

    return (
        <EditorContextProvider contextObject={contextObject} variables={variables}>
            <div style={{ width: '100%', height: '100%', position: 'relative' }} tabIndex={0} onKeyDown={handleKeyDown}>
                <ReactFlow nodes={nodes} edges={edges}
                    onNodesChange={(changes) => { onNodesChange(changes); setDirty(true); }}
                    onEdgesChange={(changes) => { onEdgesChange(changes); setDirty(true); }}
                    onConnect={onConnect} onConnectStart={onConnectStart} onConnectEnd={onConnectEnd}
                    isValidConnection={isValidConnection} nodeTypes={nodeTypes}
                    onNodeDoubleClick={handleNodeDoubleClick}
                    elementsSelectable selectionOnDrag panOnDrag={[1, 2]}
                    selectionMode={SelectionMode.Partial} selectionKeyCode={null} multiSelectionKeyCode="Shift"
                    edgesFocusable fitView proOptions={{ hideAttribution: true }}
                    style={{ background: '#1a1a1a' }} defaultEdgeOptions={{ focusable: true, interactionWidth: 20 }}>
                    <Background color="#333" gap={20} />
                    <Controls />
                </ReactFlow>
                <AddMenu onAddNode={handleAddNode} onLoadTemplate={handleLoadTemplate} />
                {connectDropMenu && (
                    <ConnectDropMenu
                        screenX={connectDropMenu.screenX}
                        screenY={connectDropMenu.screenY}
                        isFlow={connectDropMenu.isFlow}
                        sourceHandleType={connectDropMenu.sourceHandleType}
                        onSelect={handleConnectDropSelect}
                        onClose={() => { setConnectDropMenu(null); connectStartRef.current = null; }}
                    />
                )}
                <Breadcrumb path={navStack} onNavigate={handleBreadcrumb} />
                <div style={{ position: 'absolute', top: 10, right: 10, zIndex: 10, display: 'flex', gap: 6, alignItems: 'center' }}>
                    {pendingGroup ? (
                        <div style={{ display: 'flex', gap: 4, alignItems: 'center', background: '#2a2a2a', border: '1px solid #7c3aed', borderRadius: 4, padding: '4px 8px' }}>
                            <input type="text" value={groupName} onChange={(e) => setGroupName(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter') handleGroupConfirm(); if (e.key === 'Escape') setPendingGroup(null); }}
                                autoFocus placeholder="subgraph name" style={{ ...INPUT_STYLE, width: 120 }} />
                            <button onClick={handleGroupConfirm} style={{ ...TOOLBAR_BUTTON, padding: '4px 10px', background: '#7c3aed', borderColor: '#7c3aed', fontSize: 11 }}>{'\u25A3'} Create</button>
                            <button onClick={() => setPendingGroup(null)} style={{ ...TOOLBAR_BUTTON, padding: '4px 8px', fontSize: 11 }}>{'\u2715'}</button>
                        </div>
                    ) : selectedCount >= 2 ? (
                        <button onClick={handleGroupStart} style={{ ...TOOLBAR_BUTTON, background: '#7c3aed', borderColor: '#7c3aed' }}
                            title="Group the selected nodes into a subgraph — a container you name, enter and edit. Saved with this scene.">{'\u25A3'} Group</button>
                    ) : null}
                    {/*
                      * Two expand actions sit here, and they are NOT the same
                      * thing — so they are named in parallel and coloured by
                      * which kind of block they open. Subgraph = author-defined
                      * container (violet, reversible: Group re-creates it).
                      * Macro = catalog-defined composition (fuchsia, one-way).
                      */}
                    {singleSelectedSubgraph && !pendingGroup && (
                        <button onClick={() => handleInline(singleSelectedSubgraph)}
                            style={{ ...TOOLBAR_BUTTON, borderColor: '#7c3aed' }}
                            title="Expand this subgraph back into its nodes. Reversible — select them and Group to make a subgraph again.">
                            {'\u25A3'} Expand Subgraph
                        </button>
                    )}
                    {singleSelectedMacro && !pendingGroup && (
                        <button onClick={() => handleExpandMacro(singleSelectedMacro)}
                            style={{ ...TOOLBAR_BUTTON, borderColor: NODE_COLORS.macro }}
                            title="Expand this composition into the standard nodes behind it. One-way: once saved, the nodes no longer collapse back into the block.">
                            {MACRO_BADGE} Expand Macro
                        </button>
                    )}
                    <button onClick={handleSave} style={{ padding: '6px 18px', background: dirty ? '#16a34a' : '#333', border: '1px solid #555', borderRadius: 4, color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: dirty ? 600 : 400 }}>
                        {dirty ? 'Save *' : 'Saved'}
                    </button>
                </div>
                <VariablesPanel variables={variables} onChange={(vars) => { setVariables(vars); setDirty(true); }} />
            </div>
        </EditorContextProvider>
    );
}

// ============================================================================
// Main Editor — manages navigation and allGraphs, delegates rendering to GraphView
// ============================================================================

export interface BehaviorGraphEditorProps {
    graphData: GraphData;
    onSave: (graph: GraphData, allGraphs?: GraphData[]) => void;
    contextObject?: SceneObject | null;
    allGraphs?: GraphData[];
}

export function BehaviorGraphEditor({ graphData, onSave, contextObject, allGraphs: initialAllGraphs }: BehaviorGraphEditorProps): React.JSX.Element {
    const [allGraphs, setAllGraphs] = useState<GraphData[]>(() => initialAllGraphs ?? [graphData]);
    const [navStack, setNavStack] = useState<Array<{ label: string; graphIndex: number }>>([{ label: 'Main', graphIndex: 0 }]);
    // viewKey changes on every navigation to force GraphView remount
    const [viewKey, setViewKey] = useState(0);

    const currentGraphIndex = navStack[navStack.length - 1].graphIndex;
    const currentGraph = allGraphs[currentGraphIndex] ?? graphData;
    const isSubgraphView = navStack.length > 1;

    // Suppress prop-change reset when we caused the save ourselves
    const selfSaveRef = useRef(false);
    const prevGraphDataRef = useRef(graphData);
    React.useEffect(() => {
        if (graphData !== prevGraphDataRef.current) {
            prevGraphDataRef.current = graphData;
            if (selfSaveRef.current) {
                // We caused this prop change via onSave — ignore, we already have latest state
                selfSaveRef.current = false;
            } else {
                // External change (different object selected, etc.) — full reset
                setAllGraphs(initialAllGraphs ?? [graphData]);
                setNavStack([{ label: 'Main', graphIndex: 0 }]);
                setViewKey(k => k + 1);
            }
        }
    }, [graphData, initialAllGraphs]);

    const handleSaveGraph = useCallback((nodes: Node<FlowNodeData>[], edges: Edge[], variables: GraphVariable[], portEdges?: Edge[]) => {
        const { graph, allGraphs: updatedGraphs } = reactFlowToGraph(nodes, edges, variables, allGraphs, currentGraphIndex, contextObject);
        if (portEdges && portEdges.length > 0) {
            updateInterfaceFromPortEdges(updatedGraphs[currentGraphIndex], portEdges);
        }
        setAllGraphs(updatedGraphs);
        selfSaveRef.current = true;
        onSave(graph, updatedGraphs.length > 1 ? updatedGraphs : undefined);
    }, [allGraphs, currentGraphIndex, onSave, contextObject]);

    const handleNavigateIn = useCallback((
        currentNodes: Node<FlowNodeData>[], currentEdges: Edge[], currentVars: GraphVariable[],
        subgraphIndex: number, label: string,
    ) => {
        // Save current graph state internally (no onSave → no panel reload → no race)
        const { allGraphs: updated } = reactFlowToGraph(currentNodes, currentEdges, currentVars, allGraphs, currentGraphIndex, contextObject);
        setAllGraphs(updated);
        setNavStack(prev => [...prev, { label, graphIndex: subgraphIndex }]);
        setViewKey(k => k + 1);
    }, [allGraphs, currentGraphIndex, contextObject]);

    const handleNavigateOut = useCallback((
        currentNodes: Node<FlowNodeData>[], currentEdges: Edge[], currentVars: GraphVariable[],
        depth: number, currentPortEdges: Edge[],
    ) => {
        // Save current graph state internally (no onSave → no panel reload → no race)
        const { allGraphs: updated } = reactFlowToGraph(currentNodes, currentEdges, currentVars, allGraphs, currentGraphIndex, contextObject);
        // Update interface sockets from port edge wiring
        if (currentPortEdges.length > 0) {
            updateInterfaceFromPortEdges(updated[currentGraphIndex], currentPortEdges);
        }
        setAllGraphs(updated);
        setNavStack(prev => prev.slice(0, depth + 1));
        setViewKey(k => k + 1);
    }, [allGraphs, currentGraphIndex, contextObject]);

    const handleGroup = useCallback((nodes: Node<FlowNodeData>[], edges: Edge[], selectedIds: Set<string>, name: string) => {
        const result = groupIntoSubgraph(nodes, edges, selectedIds, allGraphs, [], currentGraphIndex, name, contextObject);
        // Save the grouped result as the current graph
        const parentNodes = result.nodes.filter(n => !n.id.startsWith('__port-'));
        const parentEdges = result.edges.filter(e => !e.source.startsWith('__port-') && !e.target.startsWith('__port-'));
        const { graph, allGraphs: savedGraphs } = reactFlowToGraph(parentNodes, parentEdges, [], result.allGraphs, currentGraphIndex, contextObject);
        setAllGraphs(savedGraphs);
        // Commit to behavior so the panel has the latest data (including subgraph defs)
        selfSaveRef.current = true;
        onSave(graph, savedGraphs.length > 1 ? savedGraphs : undefined);
        setViewKey(k => k + 1);
    }, [allGraphs, currentGraphIndex, onSave, contextObject]);

    const handleInline = useCallback((nodes: Node<FlowNodeData>[], edges: Edge[], nodeId: string, variables: GraphVariable[]) => {
        const result = inlineSubgraph(nodes, edges, nodeId, allGraphs, 0, variables);
        if (result.nodes === nodes) return null; // no-op
        return result;
    }, [allGraphs]);

    return (
        <div style={{ width: '100%', height: '100%', position: 'relative' }}>
            <GraphView
                key={viewKey}
                graph={currentGraph}
                allGraphs={allGraphs}
                navStack={navStack}
                isSubgraphView={isSubgraphView}
                onSaveGraph={handleSaveGraph}
                onNavigateIn={handleNavigateIn}
                onNavigateOut={handleNavigateOut}
                onGroup={handleGroup}
                onInline={handleInline}
                contextObject={contextObject ?? null}
            />
        </div>
    );
}
